import { getDifficultyOptions, getRhetoricOptions, getWritingTechniqueOptions } from '@/components/slides/SlideComponentSentenceMaker/options';
import { Popover } from '@/components/utils/Popover/Popover';
import { SlideComponentSentenceMaker } from '@/types/slide';
import { Autocomplete, Box, FormControl, FormHelperText, InputLabel, ListSubheader, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type SettingsProps = SlideComponentSentenceMaker['content'];
type LanguageVariant = SlideComponentSentenceMaker['content']['language'];

interface Props {
    anchorEl: HTMLElement | null;
    value: SettingsProps;
    onClose: () => void;
    onUpdate: (settings: SettingsProps) => void;
    slotProps?: { paper?: { sx?: object; ref: React.Ref<HTMLDivElement> } };
}

export const SettingsPopover: React.FC<Props> = ({ anchorEl, onClose, onUpdate, value, slotProps }) => {
    const { t } = useTranslation();
    const open = Boolean(anchorEl);

    const isContentValid = (value.keywords && value.keywords.length > 0) || !!value.sentencePattern || !!value.theme || !!value.writingTechnique || !!value.rhetoric;

    const themeOptions = useMemo(
        () => [
            { category: '', title: t('slide.components.sentenceMaker.theme.nature') },
            { category: '', title: t('slide.components.sentenceMaker.theme.midAutumnFestival') },
            { category: '', title: t('slide.components.sentenceMaker.theme.diary') },
            { category: '', title: t('slide.components.sentenceMaker.theme.helpingExperience') },
        ],
        [t],
    );

    const difficultyOptions = useMemo(() => getDifficultyOptions(t), [t]);

    const writingTechniqueOptions = useMemo(() => getWritingTechniqueOptions(t), [t]);

    const rhetoricOptions = useMemo(() => getRhetoricOptions(t), [t]);

    const keywordOptions = useMemo(
        () => [
            // sample themes
            { category: '', title: t('slide.components.sentenceMaker.keywords.like') },
            { category: '', title: t('slide.components.sentenceMaker.keywords.care') },
            { category: '', title: t('slide.components.sentenceMaker.keywords.explore') },
            { category: '', title: t('slide.components.sentenceMaker.keywords.enlighten') },
            { category: '', title: t('slide.components.sentenceMaker.keywords.clarity') },
        ],
        [t],
    );

    const sentencePatternOptions = useMemo(
        () => [
            // sample themes
            { category: '', title: t('slide.components.sentenceMaker.sentencePattern.sometimes') },
            { category: '', title: t('slide.components.sentenceMaker.sentencePattern.noMatterWhat') },
            { category: '', title: t('slide.components.sentenceMaker.sentencePattern.otherwise') },
            { category: '', title: t('slide.components.sentenceMaker.sentencePattern.besidesAlso') },
            { category: '', title: t('slide.components.sentenceMaker.sentencePattern.therefore') },
            { category: '', title: t('slide.components.sentenceMaker.sentencePattern.evenMoreSo') },
        ],
        [t],
    );

    const handleUpdate = (updates: Partial<SettingsProps>) => {
        onUpdate({
            ...value,
            ...updates,
        });
    };

    const currentKeywordsLength = useMemo(() => (value.keywords || []).reduce((acc, curr) => acc + curr.length, 0), [value.keywords]);

    return (
        <Popover
            title={t('slide.components.sentenceMaker.settings')}
            open={open}
            anchorEl={anchorEl}
            onClose={() => onClose()}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: -8,
                horizontal: 'center',
            }}
            slotProps={slotProps}
        >
            <Stack spacing={2} sx={{ width: 272 }}>
                <FormControl fullWidth error={!value.difficulty}>
                    <InputLabel id='difficulty-level-label' required>
                        {t('slide.components.sentenceMaker.difficultyLevel')}
                    </InputLabel>
                    <Select
                        labelId='difficulty-level-label'
                        id='difficulty-level-select'
                        label={t('slide.components.sentenceMaker.difficultyLevel')}
                        value={value.difficulty}
                        onChange={(e) => {
                            handleUpdate({ difficulty: e.target.value as string });
                        }}
                        fullWidth
                        required
                    >
                        {difficultyOptions.map((option) =>
                            'isCategory' in option && option.isCategory ? (
                                <ListSubheader key={option.value} sx={{ color: (theme) => theme.palette.secondary.main }}>
                                    {option.label}
                                </ListSubheader>
                            ) : (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ),
                        )}
                    </Select>
                    {!value.difficulty && <FormHelperText>{t('slide.components.sentenceMaker.warning.requiredField')}</FormHelperText>}
                </FormControl>

                <Box>
                    <FormControl fullWidth>
                        <InputLabel id='language-label'>{t('slide.components.sentenceMaker.language.title')}</InputLabel>
                        <Select
                            labelId='language-label'
                            id='language-select'
                            label={t('slide.components.sentenceMaker.language.title')}
                            value={value.language}
                            onChange={(e) => {
                                handleUpdate({ language: e.target.value as LanguageVariant });
                            }}
                        >
                            <MenuItem value='traditional'>{t('slide.components.sentenceMaker.language.traditional')}</MenuItem>
                            <MenuItem value='simplified'>{t('slide.components.sentenceMaker.language.simplified')}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box
                    sx={{
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <Typography variant='subtitle2' color='textSecondary'>
                        {t('slide.components.sentenceMaker.sentenceOptions')}
                    </Typography>
                    {!isContentValid && (
                        <Typography variant='caption' color='textSecondary' sx={{ mt: -1, mb: 1 }}>
                            {t('slide.components.sentenceMaker.warning.atLeastOneOption')}
                        </Typography>
                    )}

                    <Box>
                        <Autocomplete
                            multiple
                            groupBy={(option) => option.category}
                            options={keywordOptions}
                            freeSolo
                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
                            renderInput={(params) => {
                                const inputValue = params.inputProps.value as string;
                                const isMaxLengthExceeded = currentKeywordsLength + inputValue.length > value.maxWords;
                                return (
                                    <TextField
                                        {...params}
                                        placeholder={t('slide.components.sentenceMaker.keywords.placeholder')}
                                        required={!isContentValid}
                                        error={!isContentValid || isMaxLengthExceeded}
                                        helperText={isMaxLengthExceeded ? t('slide.components.sentenceMaker.warning.maxLengthExceeded', { max: value.maxWords }) : undefined}
                                        label={t('slide.components.sentenceMaker.keywords.label')}
                                    />
                                );
                            }}
                            value={value.keywords || []}
                            onChange={(_event, newValue) => {
                                const newKeywords = newValue.map((v) => (typeof v === 'string' ? v : v.title));
                                const totalLength = newKeywords.reduce((acc, curr) => acc + curr.length, 0);

                                if (totalLength <= value.maxWords) {
                                    handleUpdate({ keywords: newKeywords });
                                }
                            }}
                        />
                    </Box>

                    <Box>
                        <Autocomplete
                            aria-label='sentence-pattern'
                            fullWidth
                            freeSolo
                            options={sentencePatternOptions}
                            groupBy={(option) => option.category}
                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
                            value={value.sentencePattern || ''}
                            onChange={(_, newValue) => {
                                const sentencePatternValue = typeof newValue === 'string' ? newValue : newValue?.title || '';
                                handleUpdate({ sentencePattern: sentencePatternValue });
                            }}
                            onInputChange={(_, newInputValue) => {
                                handleUpdate({ sentencePattern: newInputValue });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t('slide.components.sentenceMaker.sentencePattern.placeholder')}
                                    label={t('slide.components.sentenceMaker.sentencePattern.label')}
                                    required={!isContentValid}
                                    error={!isContentValid}
                                />
                            )}
                        />
                    </Box>

                    <Box>
                        <Autocomplete
                            aria-label='theme'
                            fullWidth
                            freeSolo
                            options={themeOptions}
                            groupBy={(option) => option.category}
                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
                            value={value.theme || ''}
                            onChange={(_, newValue) => {
                                const themeValue = typeof newValue === 'string' ? newValue : newValue?.title || '';
                                handleUpdate({ theme: themeValue });
                            }}
                            onInputChange={(_, newInputValue) => {
                                handleUpdate({ theme: newInputValue });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t('slide.components.sentenceMaker.theme.placeholder')}
                                    label={t('slide.components.sentenceMaker.theme.label')}
                                    required={!isContentValid}
                                    error={!isContentValid}
                                />
                            )}
                        />
                    </Box>

                    <FormControl fullWidth error={!isContentValid}>
                        <InputLabel id='writing-technique-label' required={!isContentValid}>
                            {t('slide.components.sentenceMaker.writingTechnique.title')}
                        </InputLabel>
                        <Select
                            labelId='writing-technique-label'
                            id='writing-technique-select'
                            label={t('slide.components.sentenceMaker.writingTechnique.title')}
                            value={value.writingTechnique}
                            onChange={(e) => {
                                handleUpdate({ writingTechnique: e.target.value as string });
                            }}
                            fullWidth
                            required={!isContentValid}
                        >
                            {writingTechniqueOptions.map((option) =>
                                'isCategory' in option && option.isCategory ? (
                                    <ListSubheader key={option.value} sx={{ color: (theme) => theme.palette.secondary.main }}>
                                        {option.label}
                                    </ListSubheader>
                                ) : (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ),
                            )}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth error={!isContentValid}>
                        <InputLabel id='rhetoric-label' required={!isContentValid}>
                            {t('slide.components.sentenceMaker.rhetoric.title')}
                        </InputLabel>
                        <Select
                            labelId='rhetoric-label'
                            id='rhetoric-select'
                            label={t('slide.components.sentenceMaker.rhetoric.title')}
                            value={value.rhetoric}
                            onChange={(e) => {
                                handleUpdate({ rhetoric: e.target.value as string });
                            }}
                            fullWidth
                            required={!isContentValid}
                        >
                            {rhetoricOptions.map((option) =>
                                'isCategory' in option && option.isCategory ? (
                                    <ListSubheader key={option.value} sx={{ color: (theme) => theme.palette.secondary.main }}>
                                        {option.label}
                                    </ListSubheader>
                                ) : (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ),
                            )}
                        </Select>
                    </FormControl>
                </Box>
            </Stack>
        </Popover>
    );
};
